// ============================================================================
// Block Layout Style Builder — CSS from BlockLayoutConfig
// Pattern matches sectionStyleBuilder.ts
// ============================================================================

import type { CSSProperties } from 'react';
import type { BlockLayoutConfig } from '../../editor/types';
import { resolveColorHex } from '../../editor/constants/colorTokens';
import {
  BLOCK_BORDER_RADIUS,
  BLOCK_MAX_WIDTH,
  BLOCK_ALIGNMENT_CSS,
  resolveSpacingToCSS,
} from '../../editor/constants/blockLayoutDefaults';

export interface BlockLayoutStyles {
  wrapperStyle: CSSProperties;
  innerStyle: CSSProperties;
  hideOn: ('mobile' | 'desktop')[];
}

const EMPTY: BlockLayoutStyles = { wrapperStyle: {}, innerStyle: {}, hideOn: [] };

export function buildBlockLayoutStyle(
  layout: BlockLayoutConfig | null | undefined,
): BlockLayoutStyles {
  if (!layout) return EMPTY;

  const wrapperStyle: CSSProperties = {};
  const innerStyle: CSSProperties = {};

  // Alignment — positions block within parent
  if (layout.alignment) {
    wrapperStyle.justifyContent = BLOCK_ALIGNMENT_CSS[layout.alignment] ?? 'flex-start';
  }

  // Text alignment
  if (layout.text_align) {
    innerStyle.textAlign = layout.text_align;
  }

  // Max width
  if (layout.max_width && layout.max_width !== 'auto') {
    innerStyle.maxWidth = BLOCK_MAX_WIDTH[layout.max_width] ?? '100%';
    innerStyle.width = '100%';
  }

  // Padding
  const resolvedPadding = resolveSpacingToCSS(layout.padding);
  if (resolvedPadding) {
    innerStyle.padding = resolvedPadding;
  }

  // Margin
  const resolvedMargin = resolveSpacingToCSS(layout.margin);
  if (resolvedMargin) {
    innerStyle.margin = resolvedMargin;
  }

  // Background
  const bg = layout.background;
  if (bg && bg.type !== 'none') {
    if (bg.type === 'inherit') {
      // Inherit = use the consumer section's dark background (charcoal.charcoal)
      innerStyle.backgroundColor = 'var(--chakra-colors-charcoal-charcoal)';
    } else if (bg.type === 'color' && bg.color) {
      innerStyle.backgroundColor = bg.color;
    } else if (bg.type === 'gradient' && bg.gradient?.trim()) {
      innerStyle.backgroundImage = bg.gradient;
    }
  }

  // Border
  const border = layout.border;
  if (border) {
    if (border.color && border.width) {
      innerStyle.borderWidth = border.width;
      innerStyle.borderStyle = 'solid';
      innerStyle.borderColor = border.color;
    }
    if (border.radius && border.radius !== 'none') {
      innerStyle.borderRadius = BLOCK_BORDER_RADIUS[border.radius] ?? '0';
    }
  }

  // Flex properties
  if (layout.align_self && layout.align_self !== 'auto') {
    wrapperStyle.alignSelf = layout.align_self;
  }
  if (layout.flex_grow !== undefined) {
    wrapperStyle.flexGrow = layout.flex_grow;
  }
  if (layout.flex_shrink !== undefined) {
    wrapperStyle.flexShrink = layout.flex_shrink;
  }

  // Text color override: 'auto' = inherit, legacy 'light'/'dark', or color token ID
  if (layout.text_color && layout.text_color !== 'auto') {
    if (layout.text_color === 'light') {
      innerStyle.color = 'var(--chakra-colors-neutral-white)';
    } else if (layout.text_color === 'dark') {
      innerStyle.color = 'var(--chakra-colors-charcoal-charcoal)';
    } else {
      const hex = resolveColorHex(layout.text_color);
      if (hex) innerStyle.color = hex;
    }
  }

  // Responsive visibility — passed as array for Chakra responsive props
  const hideOn = layout.hide_on ?? [];

  return { wrapperStyle, innerStyle, hideOn };
}

// ---------------------------------------------------------------------------
// Responsive style builder — desktop + optional mobile override
// ---------------------------------------------------------------------------
export interface ResponsiveBlockLayoutStyles {
  desktop: BlockLayoutStyles;
  mobile: BlockLayoutStyles | null; // null = no mobile overrides, use desktop
}

const MOBILE_OVERRIDE_KEYS: Array<keyof BlockLayoutConfig> = [
  'mobile_padding', 'mobile_margin', 'mobile_alignment',
  'mobile_text_align', 'mobile_max_width',
  'mobile_align_self', 'mobile_flex_grow', 'mobile_flex_shrink',
];

export function buildResponsiveBlockLayoutStyles(
  layout: BlockLayoutConfig | null | undefined,
): ResponsiveBlockLayoutStyles {
  const desktop = buildBlockLayoutStyle(layout);

  if (!layout) return { desktop, mobile: null };

  const hasMobileOverride = MOBILE_OVERRIDE_KEYS.some((key) => layout[key] !== undefined);
  if (!hasMobileOverride) return { desktop, mobile: null };

  // Build merged mobile layout: mobile_X overrides base X, falls back to base
  const mobileLayout: BlockLayoutConfig = {
    ...layout,
    padding: layout.mobile_padding ?? layout.padding,
    margin: layout.mobile_margin ?? layout.margin,
    alignment: layout.mobile_alignment ?? layout.alignment,
    text_align: layout.mobile_text_align ?? layout.text_align,
    max_width: layout.mobile_max_width ?? layout.max_width,
    // Flex overrides
    align_self: layout.mobile_align_self ?? layout.align_self,
    flex_grow: layout.mobile_flex_grow ?? layout.flex_grow,
    flex_shrink: layout.mobile_flex_shrink ?? layout.flex_shrink,
  };

  return {
    desktop,
    mobile: buildBlockLayoutStyle(mobileLayout),
  };
}
